import { useState } from "react";
import { useTranslation } from "react-i18next";
import { DataTable } from "@/components/shared/DataTable";
import { Button } from "@/components/ui/button";
import { usePaginatedQuery } from "@/hooks/usePaginatedQuery";
import { cn } from "@/lib/utils";
import { Download, History } from "lucide-react";
import { Link } from "react-router-dom";
import type { AuditEventSummary } from "@/types/api";
import { consoleBffRequestUrl } from "@/api/client";

function resolveTargetHref(event: AuditEventSummary): string | null {
  const target = event.targetSnapshot;
  if (target.targetLinkState !== "linkable" || !target.targetId) {
    return null;
  }
  if (target.routeKind === "storage_detail") {
    return `/console/storages/${target.targetId}`;
  }
  return null;
}

const AUDIT_OUTCOME_BADGE_CLASSES: Record<string, string> = {
  requested: "bg-blue-100 text-blue-800",
  succeeded: "bg-green-100 text-green-800",
  blocked: "bg-yellow-100 text-yellow-800",
  failed: "bg-red-100 text-red-800",
};

export function AuditLogPage() {
  const { t } = useTranslation("audit-log");
  const [exportError, setExportError] = useState<string | null>(null);
  const { items, isLoading, error, hasNext, hasPrev, goNext, goPrev, refresh } =
    usePaginatedQuery<AuditEventSummary>("/audit-events");

  const handleExportCsv = async () => {
    setExportError(null);
    try {
      const res = await fetch(consoleBffRequestUrl("/console-bff/api/audit-events/export"), {
        credentials: "include",
      });
      if (!res.ok) {
        setExportError(t("export.failedHttp", { status: String(res.status) }));
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setExportError(
        e instanceof Error ? e.message : t("export.failedUnknown"),
      );
    }
  };

  const columns = [
    {
      key: "occurredAt",
      header: t("columns.when"),
      render: (e: AuditEventSummary) =>
        new Date(e.occurredAt).toLocaleString("ja-JP"),
      className: "w-[180px] align-top text-muted-foreground whitespace-nowrap",
    },
    {
      key: "actor",
      header: t("columns.who"),
      render: (e: AuditEventSummary) => (
          <div className="min-w-0 space-y-1">
            <div className="font-medium break-words">
              {e.actorSnapshot.displayName}
            </div>
            {e.actorSnapshot.email && (
              <div className="break-all text-xs text-muted-foreground">
                {e.actorSnapshot.email}
              </div>
            )}
            <div className="text-xs text-muted-foreground">
              {t(`actorTypes.${e.actorSnapshot.role}`, e.actorSnapshot.role)}
            </div>
          </div>
      ),
      className: "w-[220px] align-top",
    },
    {
      key: "target",
      header: t("columns.what"),
      render: (e: AuditEventSummary) => {
        const targetHref = resolveTargetHref(e);
        const targetName = targetHref ? (
          <Link
            className="block font-medium break-words underline-offset-2 hover:underline"
            to={targetHref}
          >
            {e.targetSnapshot.displayName}
          </Link>
        ) : (
          <div className="font-medium break-words">
            {e.targetSnapshot.displayName}
          </div>
        );
        return (
          <div className="min-w-0 space-y-1">
            {targetName}
            <div className="text-xs text-muted-foreground">
              {t(
                `targetKinds.${e.targetSnapshot.targetKind}`,
                e.targetSnapshot.targetKind,
              )}
            </div>
          </div>
        );
      },
      className: "w-[220px] align-top",
    },
    {
      key: "eventFamily",
      header: t("columns.how"),
      render: (e: AuditEventSummary) =>
        <span className="break-words">
          {t(`eventFamilies.${e.eventFamily}`, e.eventFamily)}
        </span>,
      className: "w-[220px] align-top",
    },
    {
      key: "outcome",
      header: t("columns.result"),
      render: (e: AuditEventSummary) => (
        <span
          className={cn(
            "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium",
            AUDIT_OUTCOME_BADGE_CLASSES[e.outcome] ?? "bg-gray-100 text-gray-600",
          )}
        >
          {t(`outcomes.${e.outcome}`, e.outcome)}
        </span>
      ),
      className: "w-[120px] align-top",
    },
  ];

  return (
    <div className="p-6">
      <div className="mb-6 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-2">
          <h1 className="text-2xl font-bold">{t("title")}</h1>
          <div className="flex flex-wrap gap-2 text-sm">
            <span className="rounded-full bg-slate-950 px-3 py-1 font-medium text-white">
              {t("scope.consoleOperations")}
            </span>
            <span className="rounded-full bg-muted px-3 py-1 text-muted-foreground">
              {t("scope.storageAccessPlanned")}
            </span>
          </div>
        </div>
        <div className="flex flex-col items-stretch gap-1 sm:items-end">
          <Button variant="outline" size="sm" onClick={handleExportCsv}>
            <Download className="mr-2 h-4 w-4" />
            {t("export.csv")}
          </Button>
          {exportError ? (
            <p className="text-sm text-destructive" role="alert">
              {exportError}
            </p>
          ) : null}
        </div>
      </div>

      <div className="mb-4 flex gap-3 rounded-md border bg-muted/30 p-4 text-sm text-muted-foreground">
        <History className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
        <div>
          <p className="font-medium text-foreground">
            {t("scope.currentTitle")}
          </p>
          <p>{t("scope.currentBody")}</p>
        </div>
      </div>

      {error ? (
        <div className="space-y-3 rounded-md border border-destructive/30 bg-destructive/5 p-4" role="alert">
          <p className="text-sm text-destructive">{t("load.failed")}</p>
          <Button variant="outline" size="sm" onClick={() => void refresh()}>
            {t("load.retry")}
          </Button>
        </div>
      ) : (
        <DataTable
          columns={columns}
          items={items}
          isLoading={isLoading}
          hasNext={hasNext}
          hasPrev={hasPrev}
          onNext={goNext}
          onPrev={goPrev}
          tableClassName="min-w-[960px] table-fixed"
          keyExtractor={(e) => e.auditEventId}
        />
      )}
    </div>
  );
}
